import { defineStore } from "pinia";
import { ref } from "vue";
import { useCurrentUser, useFirestore } from "vuefire";
import { doc, getDoc } from "firebase/firestore";

export const NOMOGRAM_METRICS = ["average", "last", "first"];

export const getNomogramBankPrefix = (bankId) => {
  if (bankId === "part-2-question-bank") return "";
  if (bankId === "interview-bank") return "new_modules/interview-course/";
  return `new_modules/${bankId}/`;
};

export const getNomogramHistogramDocPath = (bankId, metric) => {
  const prefix = getNomogramBankPrefix(bankId);
  return `${prefix}meta/histograms_${metric}`;
};

export const useNomogramStore = defineStore("nomogram", () => {
  const db = useFirestore();
  const currentUser = useCurrentUser();

  const histograms = ref({});
  const userMetrics = ref({});
  const loading = ref(false);
  const error = ref(null);

  const fetchHistogram = async (bankId, metric, { force = false } = {}) => {
    if (!bankId || !metric) return null;
    const key = `${bankId}:${metric}`;
    if (!force && Object.prototype.hasOwnProperty.call(histograms.value, key)) {
      return histograms.value[key];
    }

    const refDoc = doc(db, getNomogramHistogramDocPath(bankId, metric));
    const snap = await getDoc(refDoc);
    histograms.value[key] = snap.exists() ? snap.data() : null;
    return histograms.value[key];
  };

  const fetchUserMetrics = async (bankId, { force = false } = {}) => {
    const uid = currentUser.value?.uid;
    if (!uid || !bankId) return null;
    if (!force && Object.prototype.hasOwnProperty.call(userMetrics.value, bankId)) {
      return userMetrics.value[bankId];
    }

    const refDoc = doc(db, "userMetrics", uid, "banks", bankId);
    const snap = await getDoc(refDoc);
    userMetrics.value[bankId] = snap.exists() ? snap.data() : null;
    return userMetrics.value[bankId];
  };

  const fetchBankNomograms = async (bankId, { force = false } = {}) => {
    if (!bankId) return;
    loading.value = true;
    error.value = null;
    try {
      await Promise.all([
        fetchUserMetrics(bankId, { force }),
        ...NOMOGRAM_METRICS.map((metric) => fetchHistogram(bankId, metric, { force })),
      ]);
    } catch (err) {
      console.error("Failed to fetch nomograms:", err);
      error.value = err;
    } finally {
      loading.value = false;
    }
  };

  return {
    histograms,
    userMetrics,
    loading,
    error,
    fetchHistogram,
    fetchUserMetrics,
    fetchBankNomograms,
  };
});

